import {
  APP_CAREPROVIDER,
  APP_CLINIC,
  CARESEEKER_TAB_TYPE_PATIENTS,
  PATIENT_TAB_FILES,
  PATIENT_TAB_MAP,
  PATIENT_TAB_PROFILE,
  PATIENT_TAB_REHAB_FORMS,
  PATIENT_TAB_SEARCH,
  PATIENT_TAB_TIMELINE,
  PATIENT_TAB_TRANSITIONAL_CARE,
  REDIRECT_PARAM,
  SEARCH_MERGE_PRINT_SECTIONS_PARAM,
} from "core/consts";
import { PATHS } from "core/consts/pages";
import { ENV_PRODUCTION } from "core/model/config";
import { pathToUrl, replaceUrlParams } from "core/model/utils/urls";
import {
  buildAppUrl,
  getAppRoutePrefix,
} from "core/model/utils/urls/getBaseUrl";
import { AssessmentSlug, MessengerTabKinds, RehabFormName } from "core/types";
import { removeParamFromUrl } from "dsl/hooks/useRemoveQueryParam";
import { PatientTab } from "dsl/organisms/PatientNavigation";
import { generateParams } from "entries/receiver/Router/helpers";
import {
  Location,
  NavigateOptions,
  Path,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslations } from "translations";

const dashboardRoute = "/app/clinic/dashboard";

function isPath(value: Partial<Path> | string): value is Path {
  return !!(value as Partial<Path>)?.pathname;
}

export function isExitingAssessment(pathname: string, nextPathname: string) {
  if (!pathname.includes("/assessing")) return false;
  const patientId = pathname.match(/\/patient\/(.*)\/assessing/)?.[1];

  const nextPath = isPath(nextPathname) ? nextPathname.pathname : nextPathname;

  return !nextPath.includes(`/patient/${patientId}`);
}

export function checkAuctionRoute({ pathname }: { pathname: string }) {
  // check if user is in auction route
  const regex = /patient\/+(\d+)+\/auction\/+(\d+)\//;
  return regex.test(pathname);
}

export function navigateBetweenPatientAuctions({
  auctionId,
  pathname,
}: {
  auctionId: number;
  pathname: string;
}) {
  return pathname.replace(/auction\/+(\d+)\//, `auction/${auctionId}/`);
}

export function useEnhancedNavigate() {
  const location = useLocation();
  const translations = useTranslations();
  const navigate = useNavigate();

  const enhancedNavigate = (
    nextLocation: string,
    options?: NavigateOptions,
  ) => {
    const to = removeParamFromUrl("ref", nextLocation);

    if (isExitingAssessment(location.pathname, to)) {
      if (window.confirm(translations.patient.warningLeavingAssessment)) {
        return navigate(to, options);
      }
      return null;
    }
    return navigate(to, options);
  };

  return enhancedNavigate;
}

export type EnhancedNavigate = ReturnType<typeof useEnhancedNavigate>;

export const getPatientHomeUrls = ({ location }: { location: Location }) => {
  const { search } = location;
  const getPatientHomeTabUrl = ({
    auctionId,
    patientId,
    tab,
  }: {
    auctionId: number;
    patientId: number;
    tab: PatientTab;
  }) =>
    pathToUrl({
      pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/${tab}`,
      search,
    });

  return {
    goToDocumentation({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
    }) {
      return getPatientHomeTabUrl({
        auctionId,
        patientId,
        tab: PATIENT_TAB_TIMELINE,
      });
    },
    goToFiles({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
    }) {
      return getPatientHomeTabUrl({
        auctionId,
        patientId,
        tab: PATIENT_TAB_FILES,
      });
    },
    goToMap({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
    }) {
      return getPatientHomeTabUrl({
        auctionId,
        patientId,
        tab: PATIENT_TAB_MAP,
      });
    },
    goToPatientHome({
      auctionId,
      patientId,
      tab,
    }: {
      auctionId: number;
      patientId: number;
      tab: PatientTab;
    }) {
      return getPatientHomeTabUrl({ auctionId, patientId, tab });
    },
    goToProfile({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
    }) {
      return getPatientHomeTabUrl({
        auctionId,
        patientId,
        tab: PATIENT_TAB_PROFILE,
      });
    },
    goToRehabForms({
      auctionId,
      isDocsRedesignActive,
      patientId,
      type,
    }: {
      auctionId: number;
      patientId: number;
      type?: RehabFormName;
      isDocsRedesignActive?: boolean;
    }) {
      const root = isDocsRedesignActive
        ? `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/${PATIENT_TAB_FILES}`
        : `${dashboardRoute}/patient/${patientId}/auction/${auctionId}`;
      const pathname = `${root}/${PATIENT_TAB_REHAB_FORMS}`;

      return pathToUrl({
        pathname: type ? `${pathname}/${type}` : pathname,
        search,
      });
    },
    goToSearch({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
    }) {
      return getPatientHomeTabUrl({
        auctionId,
        patientId,
        tab: PATIENT_TAB_SEARCH,
      });
    },
    goToTransitionalCare({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
      isDocsRedesignActive?: boolean;
    }) {
      const root = `${dashboardRoute}/patient/${patientId}/auction/${auctionId}`;
      const pathname = `${root}/${PATIENT_TAB_TRANSITIONAL_CARE}`;

      return pathToUrl({
        pathname,
        search,
      });
    },
  };
};

const patientHomeNavigationHandlers = ({
  location,
  navigate,
}: {
  location: Location;
  navigate: EnhancedNavigate;
}) => {
  const urls = getCareseekerHandlerUrls({ location });

  return {
    goToDocumentation(patientId: number, auctionId: number) {
      navigate(urls.goToDocumentation({ auctionId, patientId }));
    },
    goToFiles(patientId: number, auctionId: number) {
      navigate(urls.goToFiles({ auctionId, patientId }));
    },
    goToMap(patientId: number, auctionId: number) {
      navigate(urls.goToMap({ auctionId, patientId }));
    },
    goToPatientHomeTab({
      auctionId,
      patientId,
      tab,
    }: {
      auctionId: number;
      patientId: number;
      tab: PatientTab;
    }) {
      navigate(urls.goToPatientHome({ auctionId, patientId, tab }));
    },
    goToProfile({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
    }) {
      navigate(urls.goToProfile({ auctionId, patientId }));
    },
    goToRehabForms({
      auctionId,
      isDocsRedesignActive,
      patientId,
      type,
    }: {
      auctionId: number;
      patientId: number;
      type?: RehabFormName;
      isDocsRedesignActive?: boolean;
    }) {
      navigate(
        urls.goToRehabForms({
          auctionId,
          isDocsRedesignActive,
          patientId,
          type,
        }),
      );
    },
    goToSearch({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
    }) {
      navigate(urls.goToSearch({ auctionId, patientId }));
    },
    goToTransitionalCare(patientId: number, auctionId: number) {
      navigate(urls.goToTransitionalCare({ auctionId, patientId }));
    },
  };
};

export const getCareseekerHandlerUrls = ({
  location,
}: {
  location: Location;
}) => {
  const { search } = location;

  return {
    changePassword() {
      return pathToUrl({
        pathname: `${dashboardRoute}/my-password-change`,
        search: `${REDIRECT_PARAM}=${encodeURIComponent(
          `${location.pathname}${location.search}`,
        )}`,
      });
    },
    goToAiChat() {
      return pathToUrl({
        pathname: `${dashboardRoute}/chat`,
        search,
      });
    },
    goToAuction({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId?: number;
    }) {
      const isAuctionRoute = checkAuctionRoute({
        pathname: location.pathname,
      });
      if (isAuctionRoute) {
        const nextPathname = navigateBetweenPatientAuctions({
          auctionId,
          pathname: location.pathname,
        });
        return pathToUrl({ pathname: nextPathname, search });
      } else {
        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}`,
          search,
        });
      }
    },
    goToContactUs() {
      return "https://recaresolutions.com/kontakt/";
    },
    goToDashboard() {
      return pathToUrl({
        pathname: dashboardRoute,
        search,
      });
    },
    goToDoctorDashboard() {
      return pathToUrl({
        pathname: dashboardRoute,
        search,
      });
    },
    goToForgotPassword() {
      return pathToUrl({
        pathname: `/forgot-password`,
        search,
      });
    },
    goToLogin() {
      return pathToUrl({ pathname: `/auth`, search });
    },
    goToOnPremise({ caseId, page }: { caseId?: string; page: string }) {
      return pathToUrl({
        pathname: `${dashboardRoute}/onpremsetup/${page}${
          caseId ? `?external_id=${caseId}` : ""
        }`,
        search: "",
      });
    },
    goToPredict() {
      return pathToUrl({
        pathname: `${dashboardRoute}/predict`,
        search,
      });
    },
    goToPredictPatientDetails(patientId: string) {
      return pathToUrl({
        pathname: `${dashboardRoute}/predict/${patientId}`,
        search,
      });
    },
    goToPrintableDashboard({ careseekerId }: { careseekerId: number }) {
      const pathname = replaceUrlParams(PATHS.APP.DASHBOARD_PRINTABLE, {
        careseeker_id: careseekerId,
      });
      return pathToUrl({ pathname, search: "" });
    },
    goToPrintableDischarge({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
    }) {
      const pathname = replaceUrlParams(PATHS.APP.PATIENT_DISCHARGE_PRINT, {
        auction_id: auctionId,
        patient_id: patientId,
      });
      return pathToUrl({ pathname, search: "print=true" });
    },
    goToPrintableSearchPanel({
      auctionId,
      patientId,
      printSections,
    }: {
      auctionId: number;
      patientId: number;
      printSections: string;
    }) {
      const pathname = replaceUrlParams(PATHS.APP.PATIENT_SEARCH_PANEL_PRINT, {
        auction_id: auctionId,
        patient_id: patientId,
      });
      return pathToUrl({
        pathname,
        search: `${SEARCH_MERGE_PRINT_SECTIONS_PARAM}=${printSections}`,
      });
    },
    goToProductionEnv() {
      return buildAppUrl({
        app: APP_CLINIC,
        env: ENV_PRODUCTION,
        path: getAppRoutePrefix(APP_CLINIC),
      });
    },
    goToProviderApp() {
      return buildAppUrl({
        app: APP_CAREPROVIDER,
        path: getAppRoutePrefix(APP_CAREPROVIDER),
      });
    },
    goToRehabFormApproval({
      auctionId,
      careseekerId,
      patientId,
    }: {
      auctionId: number;
      careseekerId: number;
      patientId: number;
    }) {
      return buildAppUrl({
        app: APP_CLINIC,
        path: `/app/clinic/${careseekerId}/rehab-forms-approval?auctionId=${auctionId}&patientId=${patientId}`,
      });
    },
    goToReport() {
      return pathToUrl({
        pathname: `${dashboardRoute}/report`,
        search,
      });
    },
    goToTodo(tabName?: string) {
      return pathToUrl({
        pathname: `${dashboardRoute}/todo/${
          tabName ? `${tabName}` : CARESEEKER_TAB_TYPE_PATIENTS
        }`,
        search: "",
      });
    },
    goToUsers() {
      return pathToUrl({
        pathname: `${dashboardRoute}/users`,
        search: "",
      });
    },
    patient: {
      goToAssessing({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId: number;
      }) {
        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/assessing/transfer`,
          search,
        });
      },
      goToassessmentSlug({
        assessmentSlug,
        auctionId,
        patientId,
        sessionId,
      }: {
        assessmentSlug?: AssessmentSlug;
        auctionId: number;
        patientId: number;
        sessionId?: string;
      }) {
        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/assessing/${
            assessmentSlug ?? ""
          }`,
          search: `${sessionId ? `session_id=${sessionId}` : ""}`,
        });
      },
      goToBarthel({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId: number;
      }) {
        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/barthel-index`,
          search,
        });
      },
      goToClinicalConsultation({
        clinicalConsultationId,
        patientId,
      }: {
        clinicalConsultationId: number;
        patientId: number;
      }) {
        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}/consultation/${clinicalConsultationId}`,
          search,
        });
      },
      goToClinicalConsultations({ patientId }: { patientId: number }) {
        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}/consultation`,
          search,
        });
      },
      goToEarlyRehab({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId: number;
      }) {
        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/early-rehab-index`,
          search,
        });
      },
      goToHome({
        auctionId,
        patientId,
      }: {
        auctionId?: number;
        patientId: number;
      }) {
        const auctionParam = auctionId ? `/auction/${auctionId}` : "";
        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}${auctionParam}`,
          search,
        });
      },
      goToRequest({
        auctionId,
        params,
        patientId,
        requestId,
      }: {
        auctionId: number;
        params?: {
          activeTab?: MessengerTabKinds;
          recommendation?: boolean;
        };
        patientId: number;
        requestId: number;
      }) {
        if (requestId <= 0) {
          console.error(
            `[DEV-14068] [goToRequest] tried to access bad request id ${requestId}`,
          );
          return "";
        }

        const recommendationParam = params?.recommendation ? "true" : undefined;

        const searchParams =
          generateParams([
            ["activeTab", params?.activeTab],
            ["recommendation", recommendationParam],
          ]) ?? "";

        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/request/${requestId}`,
          search: searchParams,
        });
      },
      goToSinger({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId: number;
      }) {
        return pathToUrl({
          pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/singer-index`,
          search,
        });
      },
      new(props?: { external_id?: string; origin?: string }) {
        let search = "";
        if (props?.external_id) {
          search = `external_id=${props.external_id}`;
          if (props?.origin) {
            search += `&origin=${props.origin}`;
          }
        }

        return pathToUrl({
          pathname: `${dashboardRoute}/patient/new`,
          search,
        });
      },
    },
    replaceByassessmentSlug(
      assessmentSlug: AssessmentSlug,
      patientId: number,
      auctionId: number,
    ) {
      return pathToUrl({
        pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/assessing/${assessmentSlug}`,
        search,
      });
    },
    replaceByHome({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId?: number;
    }) {
      return pathToUrl({
        pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}`,
        search,
      });
    },
    replaceByProfile({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId?: number;
    }) {
      return pathToUrl({
        pathname: `${dashboardRoute}/patient/${patientId}/auction/${auctionId}/profile`,
        search,
      });
    },
    ...getPatientHomeUrls({
      location,
    }),
    replaceByAuth() {
      return pathToUrl({
        pathname: `/auth`,
        search,
      });
    },
  };
};

export function getCareseekerNavigationHandlers({
  location,
  navigate,
}: {
  location: Location;
  navigate: EnhancedNavigate;
}) {
  const urls = getCareseekerHandlerUrls({ location });

  return {
    changePassword() {
      navigate(urls.changePassword());
    },
    goToAiChat() {
      navigate(urls.goToAiChat());
    },
    goToDoctorDashboard() {
      navigate(urls.goToDoctorDashboard());
    },
    goToForgotPassword() {
      navigate(urls.goToForgotPassword());
    },
    goToLogin() {
      navigate(urls.goToLogin());
    },
    goToOnPremise({ caseId, page }: { caseId?: string; page: string }) {
      navigate(urls.goToOnPremise({ caseId, page }));
    },
    goToPredict() {
      navigate(urls.goToPredict());
    },
    goToPredictPatientDetails(patientId: string) {
      navigate(urls.goToPredictPatientDetails(patientId));
    },
    goToPrintableDashboard({ careseekerId }: { careseekerId: number }) {
      navigate(urls.goToPrintableDashboard({ careseekerId }));
    },
    goToPrintableDischarge({
      auctionId,
      patientId,
    }: {
      auctionId: number;
      patientId: number;
    }) {
      navigate(urls.goToPrintableDischarge({ auctionId, patientId }));
    },
    goToPrintableSearchPanel({
      auctionId,
      patientId,
      printSections,
    }: {
      auctionId: number;
      patientId: number;
      printSections: string;
    }) {
      navigate(
        urls.goToPrintableSearchPanel({ auctionId, patientId, printSections }),
      );
    },
    goToReport() {
      navigate(urls.goToReport());
    },
    goToTodo(tabName?: string) {
      navigate(urls.goToTodo(tabName));
    },
    goToUsers() {
      navigate(urls.goToUsers());
    },
    patient: {
      goToAssessing({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId: number;
      }) {
        navigate(urls.patient.goToAssessing({ auctionId, patientId }));
      },
      goToassessmentSlug({
        assessmentSlug,
        auctionId,
        patientId,
        sessionId,
      }: {
        assessmentSlug?: AssessmentSlug;
        auctionId: number;
        patientId: number;
        sessionId?: string;
      }) {
        navigate(
          urls.patient.goToassessmentSlug({
            assessmentSlug,
            auctionId,
            patientId,
            sessionId,
          }),
        );
      },
      goToAuction({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId?: number;
      }) {
        navigate(urls.goToAuction({ auctionId, patientId }), { replace: true });
      },
      goToBarthel({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId: number;
      }) {
        navigate(urls.patient.goToBarthel({ auctionId, patientId }));
      },
      goToClinicalConsultation({
        clinicalConsultationId,
        patientId,
      }: {
        clinicalConsultationId: number;
        patientId: number;
      }) {
        navigate(
          urls.patient.goToClinicalConsultation({
            clinicalConsultationId,
            patientId,
          }),
        );
      },
      goToClinicalConsultations({ patientId }: { patientId: number }) {
        navigate(urls.patient.goToClinicalConsultations({ patientId }));
      },
      goToEarlyRehab({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId: number;
      }) {
        navigate(urls.patient.goToEarlyRehab({ auctionId, patientId }));
      },
      goToHome({
        auctionId,
        patientId,
      }: {
        auctionId?: number;
        patientId: number;
      }) {
        navigate(urls.patient.goToHome({ auctionId, patientId }), {
          state: {
            prevLocation: encodeURIComponent(
              `${location.pathname}${location.search}`,
            ),
          },
        });
      },
      goToRequest({
        auctionId,
        params,
        patientId,
        requestId,
      }: {
        auctionId: number;
        params?: {
          activeTab?: MessengerTabKinds;
          recommendation?: boolean;
        };
        patientId: number;
        requestId: number;
      }) {
        if (requestId <= 0) {
          console.error(
            `[DEV-14068] [goToRequest] tried to access bad request id ${requestId}`,
          );
          return;
        }

        navigate(
          urls.patient.goToRequest({ auctionId, params, patientId, requestId }),
        );
      },
      goToSinger({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId: number;
      }) {
        navigate(urls.patient.goToSinger({ auctionId, patientId }));
      },
      new(props?: { external_id?: string; origin?: string }) {
        navigate(urls.patient.new(props));
      },
      replaceByassessmentSlug({
        assessmentSlug,
        auctionId,
        patientId,
      }: {
        assessmentSlug: AssessmentSlug;
        auctionId: number;
        patientId: number;
      }) {
        navigate(
          urls.replaceByassessmentSlug(assessmentSlug, patientId, auctionId),
          { replace: true },
        );
      },
      replaceByHome({
        auctionId,
        patientId,
      }: {
        auctionId: number;
        patientId?: number;
      }) {
        navigate(urls.replaceByHome({ auctionId, patientId }), {
          replace: true,
        });
      },
      replaceByProfile(patientId: number, auctionId: number) {
        navigate(urls.replaceByProfile({ auctionId, patientId }), {
          replace: true,
        });
      },
      ...patientHomeNavigationHandlers({
        location,
        navigate,
      }),
    },
    replaceByAuth() {
      navigate(urls.replaceByAuth(), { replace: true });
    },
  };
}
